import { BrowserInfo } from "@/utils/browser";
import { Axios } from "@/utils/axios";
import { EpsonSettings } from "@/utils/label-layout/epson-settings";
import { runJsTemplate } from "@/utils/js-template";
import { printHumanFloat, printShortDate } from "@/utils/pretty-print";

export interface IEpsonTemplateColumn {
	key:string;
	label:string;
	maxLength?:number;
}

export interface IEpsonTemplateField {
	key:string;
	label:string;
	kind:"text" | "image" | "rows";
	maxLength?:number;
	rows?:number;
	columns?:IEpsonTemplateColumn[];
}

export interface IEpsonTemplate {
	id:string;
	name:string;
	description:string;
	label:{widthMm:number, heightMm:number};
	design:{widthMm:number, heightMm:number};
	rotation:number;
	fields:IEpsonTemplateField[];
	/** Voorgestelde koppeling per Winfakt-model, bv. mappings.SaleRow.referentie = "${value.Sale.ContactsSaleReference}" */
	mappings:{[modelName:string]:{[fieldKey:string]:string}};
	sampleData:any;
}

class EpsonPrintClass {
	async getTemplates(serviceUrl:string):Promise<IEpsonTemplate[]> {
		let origin = new URL(serviceUrl).origin;
		let result = await fetch(`${origin}/api/templates`);
		if (!result.ok) {
			throw new Error(`Label service antwoordde met HTTP ${result.status}`);
		}
		let json = await result.json();
		return json.templates || [];
	}

	/**
	 * Zet één record om naar de data van een template, op basis van de veldmapping.
	 * Sleutels met punten ("rijen.0.code") worden geneste objecten/arrays.
	 */
	buildData(settings:EpsonSettings, record:any):any {
		let data:any = {};
		for (let key of Object.keys(settings.Fields)) {
			let template = settings.Fields[key];
			if (!template) continue;
			let value = "";
			try {
				value = `${runJsTemplate(template, record, {printHumanFloat, printShortDate}) ?? ""}`;
			} catch (err) {
				console.warn(`Epson veld "${key}": fout in template`, err);
			}
			this.setPath(data, key.split("."), value);
		}
		return data;
	}

	private setPath(target:any, path:string[], value:string) {
		let current = target;
		for (let i = 0; i < path.length - 1; i++) {
			let part = path[i];
			let nextIsIndex = /^\d+$/.test(path[i + 1]);
			if (current[part] == null) {
				current[part] = nextIsIndex ? [] : {};
			}
			current = current[part];
		}
		current[path[path.length - 1]] = value;
	}

	/** Stuurt een ESC/Label-job via de Electron-server (lokaal of in servermodus) naar de printer. */
	async sendJob(job:ArrayBuffer, settings:EpsonSettings):Promise<void> {
		let electronUrl = BrowserInfo.getElectronUrl();
		if (!electronUrl) {
			throw new Error("Geen Winfakt desktop app of desktop server geconfigureerd");
		}
		if (!settings.Host) {
			throw new Error("Geen printer-host ingesteld voor deze layout");
		}
		let params = new URLSearchParams({host: settings.Host, port: `${settings.Port || 9100}`});
		await Axios.post(`${electronUrl}/print-zpl/print?${params.toString()}`, job, {
			headers: {"Content-Type": "application/octet-stream"}
		});
	}
}

export const EpsonPrint = new EpsonPrintClass();
